// ═══════════════════════════════════════════════════════════════
// HotWash — Export Utilities
// CSV, PDF, and Copy-as-text exports
// ═══════════════════════════════════════════════════════════════

import { Button, Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconCopy, IconFileTypeCsv } from '@tabler/icons-react';
import type { Observation, Incident } from '../../types';
import { TIER_META } from '../../types';

function escapeCsv(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return '"' + val.replace(/"/g, '""') + '"';
  }
  return val;
}

export function exportObservationsCSV(observations: Observation[], incidents: Incident[], filename?: string) {
  const headers = ['#', 'Tier', 'Title', 'Category', 'Phase', 'Description', 'Suggested Remedy', 'Submitted By', 'Incident', 'Date'];
  const rows = observations.map((o, i) => {
    const inc = incidents.find(inc => String(inc.id) === String(o.incidentId));
    return [
      String(i + 1),
      TIER_META[o.tier].label,
      o.title,
      o.category,
      o.phase,
      o.description,
      o.suggestedRemedy,
      o.submittedBy,
      inc?.name ?? '',
      o.datecreated ? new Date(o.datecreated).toLocaleDateString() : '',
    ].map(escapeCsv).join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `observations_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  notifications.show({ title: 'Exported', message: `${observations.length} observations exported to CSV.`, color: 'green' });
}

export function copyReportAsText(incident: Incident, observations: Observation[]) {
  const tierCounts = {
    observation: observations.filter(o => o.tier === 'observation').length,
    improvement_needed: observations.filter(o => o.tier === 'improvement_needed').length,
    problem_identified: observations.filter(o => o.tier === 'problem_identified').length,
    critical_error: observations.filter(o => o.tier === 'critical_error').length,
  };

  const lines = [
    'HOT WASH AFTER ACTION REPORT',
    '================================',
    `Incident: ${incident.name}`,
    `Type: ${incident.incidentType}`,
    `Status: ${incident.status}`,
    `Location: ${incident.location}`,
    `Date: ${new Date().toLocaleDateString()}`,
    '',
    'SUMMARY',
    '--------------------------------',
    `Total Observations: ${observations.length}`,
    `  Observation: ${tierCounts.observation}`,
    `  Improvement Needed: ${tierCounts.improvement_needed}`,
    `  Problem Identified: ${tierCounts.problem_identified}`,
    `  Critical Error: ${tierCounts.critical_error}`,
    '',
    'OBSERVATIONS',
    '--------------------------------',
    ...observations.map((o, i) => {
      const parts = [`${i + 1}. [${TIER_META[o.tier].label.toUpperCase()}] ${o.title}`];
      if (o.description) parts.push(`   ${o.description}`);
      if (o.suggestedRemedy) parts.push(`   Remedy: ${o.suggestedRemedy}`);
      parts.push(`   — ${o.submittedBy}, ${o.category}, ${o.phase}`);
      return parts.join('\n');
    }),
  ];

  navigator.clipboard.writeText(lines.join('\n'));
  notifications.show({ title: 'Copied', message: 'Report copied to clipboard.', color: 'green' });
}

interface ExportMenuProps {
  observations: Observation[];
  incidents: Incident[];
  incident?: Incident | null;
}

export default function ExportMenu({ observations, incidents, incident }: ExportMenuProps) {
  const filename = incident
    ? `${incident.name.replace(/[^a-zA-Z0-9]/g, '_')}_observations.csv`
    : undefined;

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button variant="subtle" size="xs" leftSection={<IconDownload size={14} />}>
          Export
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconFileTypeCsv size={14} />}
          onClick={() => exportObservationsCSV(observations, incidents, filename)}
        >
          Export CSV
        </Menu.Item>
        {incident && (
          <Menu.Item
            leftSection={<IconCopy size={14} />}
            onClick={() => copyReportAsText(incident, observations)}
          >
            Copy as Text
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
